import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { startLevelMeter } from "@/lib/orbLevel";
import { sceneCue } from "@/lib/sceneBus";
import { parseMapCommand, mapControl, mapControlActive } from "@/lib/mapControlBus";
import { useI18n } from "@/lib/i18n";

/* REAL GPT-Live (WebRTC) voice session — used only when the user turned "Live konuşma" on in Ayarlar.
   Billing: voice seconds on the user's own OpenAI key; business work is delegated (client delegation)
   to our existing Emergent Luna backend, so no extra paid OpenAI text model is started.
   A session starts ONLY on the user's start click. A generation counter (gen) invalidates every
   in-flight start, so a late SDP answer can never open a session — and if the server already created
   one, it is closed through the backend (sideband session.close). */
const CLOSE_WAIT_MS = 8000;   // bounded finalization window for session.closed
const START_WAIT_MS = 20000;  // session.started must arrive within this
const SPEAK_IDLE_MS = 900;

const micError = (e) => {
  const n = e?.name || "";
  if (/NotAllowed|PermissionDenied|Security/.test(n)) return "Mikrofon izni reddedildi — adres çubuğundaki kilit simgesinden izin verin.";
  if (/NotFound|DevicesNotFound/.test(n)) return "Mikrofon bulunamadı.";
  if (/NotReadable|TrackStart/.test(n)) return "Mikrofon başka bir uygulama tarafından kullanılıyor.";
  return `Mikrofon açılamadı (${n || "bilinmeyen hata"}).`;
};

/* Turkish, user-facing text for ANY transport failure. Raw provider/CDN bodies (e.g. Cloudflare's English
   "origin returned an invalid or incomplete response" page) are never shown. */
const requestError = (e) => {
  const st = e?.response?.status;
  const d = e?.response?.data;
  const detail = typeof d === "object" && d ? d.detail || d.message : null;
  if (detail && typeof detail === "string" && detail.length < 300 && !/<html|<!doctype/i.test(detail)) return detail;
  if (st === 401 || st === 403) return "Oturumunuz doğrulanamadı — sayfayı yenileyip tekrar giriş yapın.";
  if (st === 409) return "Live konuşma kapalı görünüyor (Ayarlar → Live konuşma). Oturum açılmadı.";
  if (st === 429) return "Çok fazla istek (429). Kısa süre bekleyip tekrar deneyin.";
  if (st === 504 || st === 502 || st === 520 || st === 521 || st === 523) {
    return "Sunucuya ulaşılamadı veya yanıt zaman aşımına uğradı (ağ geçidi hatası). Oturum açılmadı; tekrar deneyebilirsiniz.";
  }
  if (st >= 500) return `Sunucu hatası (${st}). Oturum açılmadı; tekrar deneyebilirsiniz.`;
  if (!st) return "Ağ bağlantısı kesildi veya istek zaman aşımına uğradı. Oturum açılmadı.";
  return `İstek başarısız (HTTP ${st}).`;
};

export const LiveSession = forwardRef(({ setOrbState, lang = "tr", onActions, compact = false, onStatus,
  onDisableLive }, apiRef) => {
  const { tx } = useI18n();
  const [status, setStatus] = useState("off"); // off | starting | live | closing | error
  const [note, setNote] = useState("");
  const [secs, setSecs] = useState(0);
  const audioRef = useRef(null);
  const latest = useRef({});
  latest.current = { lang, onActions, setOrbState, onStatus, status };
  const r = useRef({ gen: 0, pc: null, dc: null, mic: null, meter: null, vsid: null, sid: null,
    transcript: "", consumed: 0, mapScanned: 0, handled: new Set(), closing: false, timers: [], usage: null, reason: null });

  const report = useCallback((s, msg = "", extra = {}) => {
    setStatus(s); setNote(msg);
    latest.current.onStatus?.({ status: s, message: msg, ...extra });
  }, []);
  const orb = (s) => latest.current.setOrbState?.(s);
  const clearTimers = () => { r.current.timers.forEach(clearTimeout); r.current.timers = []; };
  const later = (fn, ms) => { r.current.timers.push(setTimeout(fn, ms)); };

  const send = (obj) => {
    const dc = r.current.dc;
    if (dc && dc.readyState === "open") { try { dc.send(JSON.stringify(obj)); return true; } catch (e) {} }
    return false;
  };

  // Tell the server to finalize usage (and, when the client could not confirm session.closed, to close
  // the real session itself through a sideband WebSocket). keepalive → survives unmount / page unload.
  const finalizeOnServer = (vsid, payload) => {
    if (!vsid) return;
    try {
      fetch(`${API}/voice/session/${vsid}/close`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        keepalive: true, body: JSON.stringify(payload),
      }).catch(() => {});
    } catch (e) {}
  };

  // Release every local resource: mic capture, remote audio, data channel, peer connection, meters.
  const releaseTransport = useCallback(() => {
    clearTimers();
    r.current.meter?.(); r.current.meter = null;
    r.current.mic?.getTracks().forEach((t) => t.stop()); r.current.mic = null;
    try { r.current.dc?.close(); } catch (e) {}
    try { r.current.pc?.getSenders?.().forEach((s) => s.track && s.track.stop()); } catch (e) {}
    try { r.current.pc?.close(); } catch (e) {}
    r.current.dc = null; r.current.pc = null;
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch (e) {} a.srcObject = null; a.muted = false; }
  }, []);

  const muteEverythingNow = () => { // the moment the user says stop: no more capture, no more sound
    r.current.mic?.getTracks().forEach((t) => { t.enabled = false; t.stop(); });
    const a = audioRef.current;
    if (a) { a.muted = true; a.volume = 0; }
    r.current.meter?.(); r.current.meter = null;
  };

  const stop = useCallback((opts = {}) => {
    const { graceful = true, quiet = false } = opts;
    const gen = ++r.current.gen; // invalidates any in-flight start (the channel listener is identity-based)
    const vsid = r.current.vsid;
    const wasOn = !!(r.current.pc || vsid);
    if (!wasOn) { releaseTransport(); report("off"); orb("idle"); return; }
    r.current.closing = true;
    muteEverythingNow();
    const done = (finalized, message) => {
      if (r.current.gen !== gen) return;
      finalizeOnServer(vsid, { finalized, usage_seconds: r.current.usage, reason: r.current.reason || (finalized ? "close_requested" : null) });
      releaseTransport();
      r.current.vsid = null; r.current.sid = null; r.current.closing = false; r.current.transcript = ""; r.current.consumed = 0;
      r.current.handled = new Set();
      r.current.finishClose = null; r.current.started = false;
      report("off", message || "");
      orb("idle");
      if (message && !quiet) toast.warning(message);
    };
    r.current.finishClose = done;
    if (graceful && r.current.started && send({ type: "session.close", event_id: `close_${gen}` })) {
      report("closing", "Oturum kapatılıyor — nihai kullanım alınıyor…");
      later(() => done(false, "Live oturumu kapatıldı ama nihai kullanım doğrulanamadı (session.closed gelmedi)."), CLOSE_WAIT_MS);
    } else {
      // never reached session.started (cancelled during startup / lost connection): no session.closed is
      // expected here — close best-effort and let the server finalize/kill the session via sideband.
      send({ type: "session.close", event_id: `close_${gen}` });
      done(false, graceful && r.current.started ? "Live oturumu kapatıldı; nihai kullanım sunucudan doğrulanıyor." : "");
    }
  }, [releaseTransport, report]);

  useEffect(() => () => stop({ graceful: true, quiet: true }), [stop]); // unmount (mode switch, navigation)
  useEffect(() => {
    const h = () => stop({ graceful: true, quiet: true });
    // Page unload: there is no time to wait for session.closed (timers die with the page), so close and
    // finalize IMMEDIATELY — the keepalive /close request lets the server close the real session via sideband.
    const onHide = () => stop({ graceful: false, quiet: true });
    window.addEventListener("pagehide", onHide);
    window.addEventListener("jarvis-stop-session", h);
    return () => { window.removeEventListener("pagehide", onHide); window.removeEventListener("jarvis-stop-session", h); };
  }, [stop]);

  const onDelegation = async (ev, dc) => {
    const id = ev.delegation?.id;
    if (!id || r.current.handled.has(id)) return; // never run the same task twice
    r.current.handled.add(id);
    // send ONLY the new speech since the last delegation — never the whole accumulated transcript
    const full = r.current.transcript;
    const ctx = full.slice(r.current.consumed).trim();
    r.current.consumed = full.length;
    if (!ctx) {
      send({ type: "session.commentary.append", event_id: `dlg_empty_${id}`, delegation_id: id,
             content: "İsteği net duyamadım, tekrar söyler misin?" });
      return;
    }
    send({ type: "session.thinking.append", event_id: `dlg_wait_${id}`, delegation_id: id,
           content: "İş verisi kontrol ediliyor. Sonuç gelmeden tamamlandı deme." });
    sceneCue("scan"); // a real delegated task started
    try {
      const res = await api.post("/voice/live/delegate", {
        vsession_id: r.current.vsid, delegation_id: id, transcript: ctx, language: latest.current.lang });
      if (r.current.dc !== dc || r.current.closing) return; // session was stopped meanwhile
      const d = res.data || {};
      send({ type: "session.commentary.append", event_id: `dlg_res_${id}`, delegation_id: id,
             content: (d.content || "Sonuç alınamadı.").slice(0, 1200) });
      sceneCue(d.ok ? "ok" : "error");
      if (d.ok && d.actions?.length) latest.current.onActions?.(d.actions);
    } catch (e) {
      if (r.current.dc !== dc || r.current.closing) return;
      sceneCue("error");
      send({ type: "session.commentary.append", event_id: `dlg_err_${id}`, delegation_id: id,
             content: "İş verisine ulaşamadım, bu yüzden sonucu doğrulayamıyorum." });
    }
  };

  // Live map control: when the user's speech contains a pan/zoom command, drive the OPEN map directly
  // (end-to-end — not just a spoken reply). Handled words are marked consumed so the business delegation
  // path never re-runs them. Debounced so a growing phrase ("sağ" → "sağa git") fires once.
  const scanMapCommand = () => {
    if (r.current.closing) return;
    const seg = r.current.transcript.slice(r.current.mapScanned);
    const cmd = parseMapCommand(seg);
    if (!cmd) return;
    r.current.mapScanned = r.current.transcript.length;
    if (mapControlActive()) {
      mapControl(cmd);
      r.current.consumed = Math.max(r.current.consumed, r.current.mapScanned);
    } else {
      toast.info(tx("Harita açık değil; önce bir konum açın."));
    }
  };

  const bindChannel = (dc) => {
    dc.addEventListener("message", ({ data }) => {
      if (r.current.dc !== dc) return; // events belong to the live channel, not to a UI generation
      let ev; try { ev = JSON.parse(data); } catch (e) { return; }
      switch (ev.type) {
        case "session.started":
          clearTimers();
          r.current.started = true;
          r.current.sid = ev.session?.id || r.current.sid;
          report("live", "Canlı konuşma açık — doğal konuşabilirsiniz.");
          orb("listening");
          break;
        case "session.usage.updated":
          r.current.usage = ev.usage?.seconds ?? r.current.usage;
          setSecs(r.current.usage || 0);
          break;
        case "session.input_transcript.delta":
          r.current.transcript += ev.delta || "";
          if (!r.current.closing) orb("listening");
          clearTimeout(r.current.mapT);
          r.current.mapT = setTimeout(scanMapCommand, 420);
          break;
        case "session.output_transcript.delta":
          if (r.current.closing) break;
          orb("speaking");
          clearTimeout(r.current.speakT);
          r.current.speakT = setTimeout(() => { if (r.current.dc === dc && !r.current.closing) orb("listening"); }, SPEAK_IDLE_MS);
          break;
        case "session.delegation.created":
          if (ev.delegation?.target === "client" || !ev.delegation?.target) onDelegation(ev, dc);
          break;
        case "session.closed":
          r.current.usage = ev.usage?.seconds ?? r.current.usage;
          r.current.reason = ev.reason || null;
          setSecs(r.current.usage || 0);
          clearTimers();
          if (r.current.finishClose) r.current.finishClose(true, "");
          else stop({ graceful: false, quiet: true });
          break;
        case "error":
          if (ev.error?.message) toast.error(`Live: ${ev.error.message}`);
          break;
        default:
          break;
      }
    });
  };

  const start = async () => {
    if (status !== "off" && status !== "error") return;
    const gen = ++r.current.gen;
    r.current.started = false;
    const alive = () => r.current.gen === gen;
    report("starting", "Live erişimi kontrol ediliyor…");
    orb("thinking");
    let st = null, stErr = null;
    try { st = (await api.get("/voice/status")).data; } catch (e) { stErr = requestError(e); }
    if (!alive()) return;
    if (stErr || !st?.available) {
      report("error", stErr || st?.message || "Live konuşma kullanılamıyor.", { retryable: !!stErr });
      orb("idle");
      return;
    }
    let mic;
    try { const mid = localStorage.getItem("jarvis-mic-id"); mic = await navigator.mediaDevices.getUserMedia({ audio: mid ? { deviceId: { exact: mid } } : true }); }
    catch (e) { if (alive()) { report("error", micError(e)); orb("idle"); } return; }
    if (!alive()) { mic.getTracks().forEach((t) => t.stop()); return; }
    r.current.mic = mic;
    try {
      const pc = new RTCPeerConnection();
      r.current.pc = pc;
      pc.ontrack = (e) => {
        if (!alive()) return;
        const a = audioRef.current;
        if (a) { a.muted = false; a.volume = 1; a.srcObject = e.streams[0]; a.play().catch(() => {}); }
        r.current.meter?.();
        r.current.meter = startLevelMeter(e.streams[0]); // orb halo follows the REAL played audio
      };
      pc.onconnectionstatechange = () => {
        if (!alive()) return;
        if (["failed", "disconnected"].includes(pc.connectionState) && !r.current.closing) {
          report("error", "Live bağlantısı koptu; nihai kullanım doğrulanmadı.");
          stop({ graceful: false, quiet: true });
        }
      };
      mic.getTracks().forEach((t) => pc.addTrack(t, mic));
      const dc = pc.createDataChannel("oai-events");
      r.current.dc = dc;
      bindChannel(dc);

      report("starting", "Mikrofon hazır — oturum açılıyor…");
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (pc.iceGatheringState !== "complete") {
        await new Promise((res) => {
          const chk = () => { if (pc.iceGatheringState === "complete") { pc.removeEventListener("icegatheringstatechange", chk); res(); } };
          pc.addEventListener("icegatheringstatechange", chk);
          setTimeout(res, 8000);
        });
      }
      if (!alive()) return;
      const res = await api.post("/voice/session", { sdp: pc.localDescription.sdp });
      const data = res.data || {};
      if (data.ok === false) { // provider/key/quota problem reported honestly as HTTP 200
        releaseTransport();
        report("error", data.message || "Live oturumu açılamadı.", { retryable: !!data.retryable });
        orb("idle");
        return;
      }
      const vsid = data.vsession_id;
      if (!alive()) {
        // The user stopped (or switched mode) while the offer was in flight: never attach this session —
        // and close the REAL session that the server created, so nothing keeps running or billing.
        finalizeOnServer(vsid, { finalized: false, reason: "aborted_by_user" });
        return;
      }
      r.current.vsid = vsid;
      r.current.sid = res.data?.session?.id || null;
      const answer = res.data?.transport?.sdp;
      if (!answer) throw new Error("GPT-Live yanıtında SDP answer yok");
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
      if (!alive()) return;
      report("starting", "Oturum başlatılıyor…");
      later(() => {
        if (r.current.gen === gen && !r.current.started) {
          report("error", "Live oturumu başlamadı (session.started gelmedi).");
          stop({ graceful: true, quiet: true });
        }
      }, START_WAIT_MS);
    } catch (e) {
      if (!alive()) return;
      finalizeOnServer(r.current.vsid, { finalized: false, reason: "start_failed" });
      r.current.vsid = null;
      releaseTransport();
      // The create response may have been lost on the way back (gateway timeout): make sure no session of
      // this user stays open on the provider side. This closes leftovers; it never opens one.
      api.post("/voice/sessions/cleanup").catch(() => {});
      report("error", requestError(e), { retryable: true });
      orb("idle");
    }
  };

  const isOn = () => ["starting", "live", "closing"].includes(latest.current.status);
  const toggle = useCallback(() => { // one session only: a rapid double tap cannot open two
    if (r.current.toggling) return;
    r.current.toggling = true;
    setTimeout(() => { r.current.toggling = false; }, 600);
    if (isOn()) stop(); else start();
  }, [stop]); // eslint-disable-line react-hooks/exhaustive-deps
  useImperativeHandle(apiRef, () => ({ start, stop, toggle, isOn }), [toggle, stop]); // eslint-disable-line react-hooks/exhaustive-deps

  // live seconds ticker is driven by server usage events only (no invented numbers)
  const mmss = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
  const on = status === "live" || status === "starting" || status === "closing";
  // a real failure must never be silent, but it is a short toast — not permanent text on the scene
  useEffect(() => { if (status === "error" && note) toast.error(note); }, [status, note]);

  return (
    <div className="flex flex-col items-center gap-2" data-testid="live-session">
      <audio ref={audioRef} autoPlay className="hidden" data-testid="live-remote-audio" />
      <div className="flex items-center gap-2">
        {/* No visible connection text / elapsed timer on the scene — the orb animation conveys the active state.
            Kept sr-only for accessibility and tests; internal usage seconds still tracked via onStatus. */}
        <span className="sr-only" data-testid="live-conn-state" data-state={status} data-secs={secs}>
          {status === "live" ? `bağlı · ${mmss(secs)}` : status === "starting" ? "bağlanıyor"
            : status === "closing" ? "kapatılıyor" : status === "error" ? "hata" : "bağlı değil"}
        </span>
        {on && compact && ( // scene (and its orb) hidden: keep access to stopping the live session
          <button onClick={() => stop()} data-testid="voice-stop-inline" disabled={status === "closing"}
            className="text-[0.66rem] px-3 py-1 rounded-full border border-red-400/35 text-red-200 hover:bg-red-500/10 disabled:opacity-60">
            {tx("Konuşmayı durdur")}
          </button>
        )}
      </div>
      {/* Only a REAL failure is shown (and toasted); idle shows nothing at all. */}
      {status === "error" ? (
        <div className="text-[0.7rem] text-center max-w-md text-red-300" data-testid="live-session-status" data-phase={status}>{note}</div>
      ) : (
        <span className="sr-only" data-testid="live-session-status" data-phase={status}>{note}</span>
      )}
      {status === "error" && (
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <button onClick={start} data-testid="live-retry-btn"
            className="text-[0.66rem] px-3 py-1.5 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10">
            {tx("Tekrar dene")}
          </button>
          <button onClick={() => onDisableLive?.()} data-testid="live-fallback-btn"
            className="text-[0.66rem] px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-200 hover:bg-amber-500/10">
            {tx("Emergent kredili ses moduna dön")}
          </button>
        </div>
      )}
      {on && !compact && null /* no persistent fine print: the orb shows the state */}
    </div>
  );
});
LiveSession.displayName = "LiveSession";

export default LiveSession;
